import { useEffect, useMemo, useState } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import SegmentedControl from '@/components/ui/SegmentedControl';
import { useSports, useFormats } from '@/features/reference/useReference';
import { useFormatGroups } from '@/features/reference/formatGroups';
import { CycleDetail, CyclePayload, CycleRole } from '@/api/cyclesService';

interface CycleSettingsFormProps {
  initial: CycleDetail | null;
  isSaving: boolean;
  onSubmit: (payload: CyclePayload) => Promise<unknown>;
  onSaved?: (result: unknown) => void;
  onCancel?: () => void;
  submitLabel?: string;
  resetToken?: unknown;
}

type Matrix = Record<number, Record<string, string>>;

const parseWeeks = (value: string, fallback: number): number => {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 ? n : fallback;
};

/** R40: the role governs the cycle's content, so the coach states it rather than the app guessing. */
const ROLES: { value: CycleRole; label: string; hint: string }[] = [
  { value: 'NORMAL', label: 'Normal', hint: 'Cycle de préparation ordinaire.' },
  { value: 'TEST', label: 'Tests', hint: 'Contient au moins une séance de test. Tests activés : seuls les tests sont gardés ; désactivés : seules les autres séances.' },
  { value: 'RECOVERY', label: 'Récupération', hint: 'Se place après la course, en fin de plan spé, et n’est jamais raccourci.' },
];

const CycleSettingsForm = ({
  initial,
  isSaving,
  onSubmit,
  onSaved,
  onCancel,
  submitLabel = 'Enregistrer',
  resetToken,
}: CycleSettingsFormProps) => {
  const { data: sports = [] } = useSports();
  const { data: formats = [] } = useFormats();
  const groups = useFormatGroups();
  // v7: one race type open at a time keeps the 14 format columns readable.
  const [raceTypeId, setRaceTypeId] = useState<number | null>(null);
  const activeGroup = groups.find((g) => g.raceType.id === raceTypeId) ?? groups[0];
  const shownFormats = activeGroup?.formats ?? [];

  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [role, setRole] = useState<CycleRole>('NORMAL');
  const [enableScaling, setEnableScaling] = useState(false);
  const [minWeeks, setMinWeeks] = useState('2');
  const [maxWeeks, setMaxWeeks] = useState('8');
  const [selectedSports, setSelectedSports] = useState<string[]>([]);
  const [matrix, setMatrix] = useState<Matrix>({});
  const [error, setError] = useState('');

  useEffect(() => {
    setError('');
    setName(initial?.name ?? '');
    setDisplayName(initial?.displayName ?? '');
    setIdentifier(initial?.identifier ?? '');
    setRole(initial?.role ?? 'NORMAL');
    setEnableScaling(initial?.enableScaling ?? false);
    setMinWeeks(String(initial?.minWeekNumber ?? 2));
    setMaxWeeks(String(initial?.maxWeekNumber ?? 8));
    setSelectedSports(initial?.sports ?? []);
    const next: Matrix = {};
    (initial?.weekPriorities ?? []).forEach((wp) => {
      (next[wp.weekNumber] ??= {})[wp.formatId] = String(wp.priority);
    });
    setMatrix(next);
  }, [initial, resetToken]);

  const maxWeek = parseWeeks(maxWeeks, 0);
  const rows = useMemo(() => Array.from({ length: maxWeek }, (_, i) => i + 1), [maxWeek]);

  const cell = (week: number, formatId: string): string => matrix[week]?.[formatId] ?? '';
  const setCell = (week: number, formatId: string, value: string) =>
    setMatrix((prev) => ({ ...prev, [week]: { ...prev[week], [formatId]: value } }));

  const autoFill = (formatId: string) =>
    setMatrix((prev) => {
      const next = { ...prev };
      rows.forEach((week, index) => {
        next[week] = { ...next[week], [formatId]: String(index + 1) };
      });
      return next;
    });

  const toggleSport = (identifierValue: string) =>
    setSelectedSports((prev) =>
      prev.includes(identifierValue) ? prev.filter((s) => s !== identifierValue) : [...prev, identifierValue]
    );

  const resulting = useMemo(
    () =>
      shownFormats.map((format) => {
        const weeks = rows
          .map((week) => ({ week, priority: Number(cell(week, format.id)) }))
          .filter((entry) => entry.priority >= 1)
          .sort((a, b) => a.priority - b.priority);
        return { format, count: weeks.length, weeks };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shownFormats, rows, matrix]
  );

  const handleSubmit = async () => {
    setError('');
    const min = parseWeeks(minWeeks, 0);
    const max = parseWeeks(maxWeeks, 0);
    if (!name.trim()) return setError('Le nom est requis.');
    // R39: any length is authorable now, odd included — only the ordering still has to hold.
    if (min < 1 || max < 1) return setError('Le nombre de semaines doit être supérieur ou égal à 1.');
    if (max < min) return setError('Le maximum de semaines doit être supérieur ou égal au minimum.');

    const weekPriorities: CyclePayload['weekPriorities'] = [];
    for (const week of rows) {
      for (const format of formats) {
        const raw = cell(week, format.id).trim();
        if (!raw) continue;
        const priority = Number(raw);
        if (!Number.isInteger(priority) || priority < 1) {
          return setError('Les priorités doivent être des entiers supérieurs ou égaux à 1.');
        }
        weekPriorities.push({ formatId: format.id, weekNumber: week, priority });
      }
    }
    for (const format of formats) {
      const ranks = weekPriorities
        .filter((wp) => wp.formatId === format.id)
        .map((wp) => wp.priority)
        .sort((a, b) => a - b);
      if (!ranks.every((rank, i) => rank === i + 1)) {
        return setError(`Format ${format.slug} : les rangs doivent être uniques et contigus à partir de 1.`);
      }
    }

    try {
      const result = await onSubmit({
        name: name.trim(),
        displayName: displayName.trim() || null,
        identifier: identifier.trim() || undefined,
        role,
        enableScaling,
        minWeekNumber: min,
        maxWeekNumber: max,
        sports: selectedSports,
        weekPriorities,
      });
      onSaved?.(result);
    } catch (err) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Enregistrement impossible.'
      );
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input label="Nom du cycle" placeholder="Base Endurance" value={name} onChange={(e) => setName(e.target.value)} />
        <Input
          label="Identifiant"
          placeholder="BASE_ENDURANCE (auto depuis le nom si vide)"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
        />
        <div className="sm:col-span-2">
          <Input
            label="Nom affiché (athlète)"
            placeholder={name.trim() ? `Vide : « ${name.trim()} »` : 'Vide : nom du cycle'}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </div>
        <Input label="Semaines min" type="number" min={1} value={minWeeks} onChange={(e) => setMinWeeks(e.target.value)} />
        <Input label="Semaines max" type="number" min={1} value={maxWeeks} onChange={(e) => setMaxWeeks(e.target.value)} />
      </section>

      <section className="flex flex-col gap-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Rôle du cycle</span>
        <div className="flex flex-wrap gap-2">
          {ROLES.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setRole(r.value)}
              className={
                role === r.value
                  ? 'rounded-lg bg-brand-500 px-3 py-1.5 text-sm text-white'
                  : 'rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 dark:border-gray-800 dark:text-gray-200'
              }
            >
              {r.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400">{ROLES.find((r) => r.value === role)?.hint}</p>
      </section>

      <section className="flex flex-col gap-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sports</span>
        <div className="flex flex-wrap gap-3">
          {sports.map((sport) => (
            <label
              key={sport.identifier}
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 dark:border-gray-800 dark:text-gray-200"
            >
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={selectedSports.includes(sport.identifier)}
                onChange={() => toggleSport(sport.identifier)}
              />
              {sport.label}
            </label>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-1">
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            className="rounded border-gray-300"
            checked={enableScaling}
            onChange={(e) => setEnableScaling(e.target.checked)}
          />
          Activer le scaling pour ce cycle
        </label>
        <p className="text-xs text-gray-400">
          Désactivé : les séances gardent leur durée saisie, même si un facteur de scaling existe pour le format.
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Priorité par semaine et par format
          </span>
        </div>
        <p className="text-xs text-gray-400">
          Pour un format, les <em>min</em> semaines les mieux classées sont toujours incluses. Les suivantes sont
          ajoutées dans l'ordre quand l'athlète a plus de temps. Une case vide : semaine jamais utilisée pour ce format.
        </p>
        {groups.length > 1 && (
          <div className="overflow-x-auto">
            <SegmentedControl
              size="sm"
              value={String(activeGroup?.raceType.id ?? '')}
              options={groups.map((g) => ({ value: String(g.raceType.id), label: g.raceType.label }))}
              onChange={(v) => setRaceTypeId(Number(v))}
            />
          </div>
        )}
        {rows.length === 0 ? (
          <p className="text-sm text-gray-400">Définissez un maximum de semaines pour afficher la matrice.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500 dark:border-gray-800">
                  <th className="px-3 py-2 font-medium">Semaine</th>
                  {shownFormats.map((format) => (
                    <th key={format.id} className="px-3 py-2 font-medium">
                      <div className="flex flex-col gap-1">
                        <span className="normal-case">{format.slug}</span>
                        <button
                          type="button"
                          onClick={() => autoFill(format.id)}
                          className="text-left text-[11px] normal-case text-brand-600 hover:underline dark:text-brand-300"
                        >
                          Remplir 1→N
                        </button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((week) => (
                  <tr key={week} className="border-b border-gray-100 last:border-0 dark:border-gray-800">
                    <td className="whitespace-nowrap px-3 py-2 text-gray-600 dark:text-gray-300">Semaine {week}</td>
                    {shownFormats.map((format) => (
                      <td key={format.id} className="px-2 py-1.5">
                        <input
                          type="number"
                          min={1}
                          value={cell(week, format.id)}
                          onChange={(e) => setCell(week, format.id, e.target.value)}
                          className="w-16 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                          placeholder="—"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {rows.length > 0 && (
        <section className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800/50">
          <span className="text-xs font-medium uppercase text-gray-500">
            Longueur résultante par format{activeGroup ? ` · ${activeGroup.raceType.label}` : ''}
          </span>
          <div className="mt-2 flex flex-col gap-1.5">
            {resulting.map(({ format, count, weeks }) => {
              const min = parseWeeks(minWeeks, 0);
              const upTo = Math.min(count, maxWeek);
              return (
                <div key={format.id} className="flex flex-col gap-0.5 text-sm">
                  <div className="flex items-center gap-3">
                    <span className="w-20 shrink-0 font-semibold text-gray-700 dark:text-gray-200">{format.slug}</span>
                    <span className="flex flex-wrap gap-1">
                      {weeks.length === 0 ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        weeks.map(({ week }, i) => (
                          <span
                            key={week}
                            className={i < min ? 'rounded bg-brand-500 px-1.5 py-0.5 text-xs text-white' : 'rounded bg-brand-500/40 px-1.5 py-0.5 text-xs text-white'}
                          >
                            {week}
                          </span>
                        ))
                      )}
                    </span>
                    <span className="ml-auto whitespace-nowrap text-xs text-gray-500">
                      {count === 0 ? 'non utilisé' : `${min} semaine(s) obligatoire(s) · jusqu'à ${upTo}`}
                    </span>
                  </div>
                  {count > 0 && count < min && (
                    <span className="pl-[5.75rem] text-xs text-warning-600">
                      {format.slug} : seulement {count} semaine(s) classée(s) pour un minimum de {min} — ce cycle
                      ne pourra pas être utilisé dans un plan {format.slug}.
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {initial && <p className="text-xs text-gray-400">Utilisé dans {initial.usedInCount} plan(s).</p>}
      {error && <p className="text-sm text-error-500">{error}</p>}

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>
            Annuler
          </Button>
        )}
        <Button onClick={handleSubmit} disabled={isSaving}>
          {isSaving ? 'Enregistrement…' : submitLabel}
        </Button>
      </div>
    </div>
  );
};

export default CycleSettingsForm;
